import {
  getConversation,
  getResults,
  getExportUrl,
  getExportTemplates,
  updateConversationItem,
  deleteConversation,
  updateConversation,
} from '../api.js';
import { getCommonCopy, getConversationCopy, getQaChatCopy, getRequirementsListCopy } from '../i18n.js';
import { openDownloadOverlay } from '../utils/downloadOverlay.js';
import { showError, showSuccess } from '../utils/toast.js';
import { registerViewCleanup } from '../utils/viewLifecycle.js';
import { createQaChatPanel } from '../components/QaChatPanel.js';
import { conversationQaContext } from '../components/qa/context.js';
import { eventTypeOrder, isEventsV2Enabled, labelForEventSubtype, labelForEventType, normalizeEventType } from '../utils/eventTypeLabels.js';

const html = String.raw;
const appConfig = window.__ANFORDERUNGSAI_CONFIG__ ?? {};
const EVENTS_V2_ENABLED = isEventsV2Enabled(appConfig);
const BACK_KEY = 'anforderungsai:conversation:back';
const EDIT_KEY = 'anforderungsai:conversation:edit';
const REQUIREMENT_STATUS_ORDER = ['NEW', 'OPEN', 'BACKLOG', 'IN_PROGRESS', 'DONE', 'ARCHIVED'];
const formatRequirementStatus = (status) => String(status ?? 'NEW').toUpperCase();

const getConversationEngineLabel = (conversation) => {
  const engine = conversation?.transcriptionEngine ?? conversation?.engine;
  if (!engine) return '—';
  const normalized = String(engine).toLowerCase();
  if (normalized === 'aws-transcribe') return 'AWS Transcribe';
  if (normalized === 'mistral' || normalized === 'voxtral') return 'Mistral (Voxtral)';
  return String(engine);
};

const getConversationSourceLabel = (conversation) => {
  const sessionType = conversation.sessionType ? String(conversation.sessionType).toLowerCase() : '';
  const mode = conversation.mode ? String(conversation.mode).toLowerCase() : '';
  const status = conversation.status ? String(conversation.status).toUpperCase() : '';
  if (sessionType === 'appfactory' || mode === 'appfactory' || conversation.appFactorySessionId) return 'App Factory';
  if (mode === 'live' || status === 'LIVE_ACTIVE') return 'Live';
  if (mode === 'transcript') return 'Transcript';
  if (conversation.originalFilename) return 'Upload';
  if (!mode || mode === 'upload' || mode === 'batch' || mode === 'standard') return 'Upload';
  return 'Unbekannt';
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const escapeAttribute = (value) => escapeHtml(value).replace(/"/g, '&quot;');

const normalizeValue = (value) => String(value ?? '').trim();
const toKey = (value) => normalizeValue(value).toLowerCase();
const toTopicKey = (value) => toKey(value).replace(/[\s-]+/g, '_');
const normalizeList = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeValue(entry)).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value.split(',').map((entry) => normalizeValue(entry)).filter(Boolean);
  }
  return [];
};

const parseAcceptance = (value) =>
  String(value ?? '')
    .split('\n')
    .map((line) => line.replace(/^\s*([-*]|\d+[.)])\s*/g, '').trim())
    .filter(Boolean);

const formatTemplate = (template, replacements = {}) => {
  if (!template) return '';
  return template.replace(/{{(.*?)}}/g, (_, key) => {
    const trimmed = key.trim();
    const value = replacements[trimmed];
    return value === undefined || value === null ? '' : value;
  });
};

const uniqueByKey = (values) => {
  const map = new Map();
  values.forEach((value) => {
    const label = normalizeValue(value);
    if (!label) return;
    const key = toKey(label);
    if (!map.has(key)) {
      map.set(key, label);
    }
  });
  return Array.from(map.entries()).map(([key, label]) => ({ key, label }));
};

const uniqueTopics = (values) => {
  const map = new Map();
  values.forEach((value) => {
    const label = normalizeValue(value);
    if (!label) return;
    const key = toTopicKey(label);
    if (!map.has(key)) {
      map.set(key, label);
    }
  });
  return Array.from(map.values());
};

const formatPriorityLabel = (value, copy) => {
  const label = normalizeValue(value);
  if (!label) return '';
  const key = toKey(label);
  return copy.filters?.priorityOptions?.[key] ?? label;
};

const formatTopicLabel = (value, copy) => {
  const raw = normalizeValue(value);
  if (!raw) return '';
  const key = toTopicKey(raw);
  const translated = copy.filters?.topicLabels?.[key];
  if (translated) return translated;
  const cleaned = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned
    .split(' ')
    .map((word) => (word ? `${word[0].toUpperCase()}${word.slice(1)}` : ''))
    .join(' ');
};

const formatCategoryLabel = (value, copy) => {
  const normalized = normalizeValue(value);
  if (!normalized) return '';
  if (EVENTS_V2_ENABLED) {
    return labelForEventType(normalized, copy?.requirementsList?.types ?? copy?.types ?? {});
  }
  return normalized;
};

const formatSubtypeLabel = (value, copy) => {
  const labels = copy?.requirementsList?.types ?? copy?.types ?? {};
  return labelForEventSubtype(value, labels);
};

const TERMINAL_CONVERSATION_STATUSES = new Set(['READY', 'COMPLETED', 'FAILED', 'ERROR', 'CANCELED']);
const STATUS_PROGRESS_MAP = {
  UPLOADED: { percent: 10, state: 'running', phase: 'uploaded' },
  PENDING: { percent: 10, state: 'running', phase: 'uploaded' },
  PROCESSING: { percent: 25, state: 'running', phase: 'processing' },
  RUNNING: { percent: 25, state: 'running', phase: 'processing' },
  TRANSCRIBING: { percent: 45, state: 'running', phase: 'transcribing' },
  EXTRACTING: { percent: 75, state: 'running', phase: 'extracting' },
  LIVE_ACTIVE: { percent: 60, state: 'running', phase: 'live' },
  LIVE_COMPLETED: { percent: 90, state: 'running', phase: 'finalizing' },
  READY: { percent: 100, state: 'success', phase: 'finalized' },
  COMPLETED: { percent: 100, state: 'success', phase: 'finalized' },
  FAILED: { percent: 100, state: 'error', phase: 'failed' },
  ERROR: { percent: 100, state: 'error', phase: 'failed' },
  CANCELED: { percent: 100, state: 'error', phase: 'failed' },
};

const isTerminalConversationStatus = (status) =>
  TERMINAL_CONVERSATION_STATUSES.has(normalizeValue(status).toUpperCase());

const clampProgressPercent = (value) => Math.max(0, Math.min(100, Math.round(value)));

const normalizeProgressState = (value) => {
  const normalized = normalizeValue(value).toLowerCase();
  if (normalized === 'success' || normalized === 'error' || normalized === 'running') {
    return normalized;
  }
  return null;
};

const deriveProgressFromStatus = (status) => {
  const normalized = normalizeValue(status).toUpperCase();
  return STATUS_PROGRESS_MAP[normalized] ?? { percent: 0, state: 'running', phase: 'unknown' };
};

const getProgressPhaseLabel = (phase, copy) => {
  const phases = copy?.progress?.phases ?? {};
  const normalized = normalizeValue(phase).toLowerCase() || 'unknown';
  return phases[normalized] ?? phases.unknown ?? normalized;
};

const getConversationProgress = (conversation) => {
  const derived = deriveProgressFromStatus(conversation?.status);
  const providedPercent = Number(conversation?.progressPercent);
  return {
    percent: Number.isFinite(providedPercent) ? clampProgressPercent(providedPercent) : derived.percent,
    state: normalizeProgressState(conversation?.progressState) ?? derived.state,
    phase: normalizeValue(conversation?.progressPhase).toLowerCase() || derived.phase,
  };
};

const getStatusClass = (status) => {
  const upper = normalizeValue(status).toUpperCase();
  if (['READY', 'COMPLETED', 'DONE'].includes(upper)) return 'success';
  if (['FAILED', 'ERROR'].includes(upper)) return 'error';
  return 'running';
};

const renderStatusProgress = (conversation, copy) => {
  const progress = getConversationProgress(conversation);
  const statusLabel = copy?.progress?.label ?? copy?.header?.status ?? 'Status';
  const phaseLabel = getProgressPhaseLabel(progress.phase, copy);
  const statusClass = getStatusClass(conversation?.status);

  return html`
    <div class="status-progress-pill state-${escapeAttribute(progress.state)}">
      <span class="status-progress-fill" style="width:${progress.percent}%" aria-hidden="true"></span>
      <div class="status-progress-content">
        <span class="status-progress-label">${escapeHtml(statusLabel)}:</span>
        <span class="status-chip ${statusClass}">${escapeHtml(conversation?.status ?? '—')}</span>
        <span class="status-progress-percent">${progress.percent}%</span>
        <span class="status-progress-phase">${escapeHtml(phaseLabel)}</span>
      </div>
    </div>
  `;
};

const formatConversationTitle = (conversation, copy) => {
  const customer = normalizeValue(conversation?.customerId || conversation?.customer);
  const project = normalizeValue(conversation?.projectId || conversation?.project);
  if (customer && project) return `${customer} · ${project}`;
  return customer || project || copy.title;
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '–');
const getTodayDateString = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};
const formatMeetingDate = (value) => {
  if (!value) return '–';
  const normalized = normalizeValue(value);
  if (!normalized) return '–';
  const parsed = Date.parse(`${normalized}T00:00:00`);
  if (!Number.isFinite(parsed)) return normalized;
  return new Date(parsed).toLocaleDateString();
};

const normalizeSummaryLine = (value) => {
  if (typeof value !== 'string') return '';
  return value.trim().replace(/\s+/g, ' ');
};

const normalizeSummaryList = (value, maxItems = 8) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const output = [];
  value.forEach((entry) => {
    const normalized = normalizeSummaryLine(entry);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    output.push(normalized);
  });
  return output.slice(0, maxItems);
};

const normalizeExecutiveSummary = (value) => {
  if (!value || typeof value !== 'object') return null;
  const overview = normalizeSummaryLine(value.overview);
  const keyRequirements = normalizeSummaryList(value.keyRequirements, 8);
  const openDecisions = normalizeSummaryList(value.openDecisions, 8);
  const risks = normalizeSummaryList(value.risks, 8);
  const nextSteps = normalizeSummaryList(value.nextSteps, 8);
  const highlights = normalizeSummaryList(value.highlights, 3);
  if (!overview && !keyRequirements.length && !openDecisions.length && !risks.length && !nextSteps.length) {
    return null;
  }
  return {
    overview,
    keyRequirements,
    openDecisions,
    risks,
    nextSteps,
    highlights,
  };
};

const splitSummarySentences = (value, maxItems = 3) => {
  const text = normalizeSummaryLine(value);
  if (!text) return [];
  const sentences = text
    .split(/(?<=[.!?])\s+/)
    .map((entry) => normalizeSummaryLine(entry))
    .filter(Boolean);
  if (sentences.length) {
    return sentences.slice(0, maxItems);
  }
  return [text];
};

const buildSummaryPreviewSentences = (executiveSummary, summary) => {
  if (Array.isArray(executiveSummary?.highlights) && executiveSummary.highlights.length) {
    return executiveSummary.highlights.slice(0, 3);
  }
  return splitSummarySentences(summary || executiveSummary?.overview || '', 3);
};

const formatSourceRange = (sourceRange, copy) => {
  const start = Number(sourceRange?.startSec);
  const end = Number(sourceRange?.endSec);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return '';
  const speaker = normalizeValue(sourceRange?.speaker);
  const time = `${start.toFixed(1)}s – ${end.toFixed(1)}s`;
  return `${copy.requirements.source}: ${time}${speaker ? ` · ${copy.requirements.speaker} ${speaker}` : ''}`;
};

const copyToClipboard = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const buildItemCopyText = (item, conversation, copy) => {
  if (!item) return '';
  const lines = [];
  const addLine = (label, value) => {
    const normalized = normalizeValue(value);
    if (!normalized) return;
    lines.push(`${label}: ${normalized}`);
  };
  const addList = (label, values) => {
    const cleaned = Array.isArray(values)
      ? values
      : typeof values === 'string'
        ? values.split('\n')
        : [];
    const normalized = cleaned.map((entry) => normalizeValue(entry)).filter(Boolean);
    if (!normalized.length) return;
    lines.push(`${label}:`);
    normalized.forEach((entry) => lines.push(`- ${entry}`));
  };
  const priorityLabel = item.priority ? formatPriorityLabel(item.priority, copy) : '';
  const topics = (item.topics ?? []).map((topic) => formatTopicLabel(topic, copy)).filter(Boolean);
  addLine(copy.sections?.requirements ?? 'Anforderungstyp', item.kindLabel);
  if (item.kind === 'userStory') {
    const story = item.data ?? {};
    addLine(copy.edit?.fields?.story ?? 'User Story', item.text || story.story);
    addLine(copy.edit?.fields?.role ?? 'Rolle', story.role);
    addLine(copy.edit?.fields?.goal ?? 'Ziel', story.goal);
    addLine(copy.edit?.fields?.benefit ?? 'Nutzen', story.benefit);
    addList(copy.edit?.fields?.acceptance ?? 'Akzeptanzkriterien', story.acceptance);
  } else {
    const req = item.data ?? {};
    addLine(copy.edit?.fields?.text ?? 'Text', item.text || req.text || req.requirement);
    if (req.rationale) {
      addLine(copy.requirements?.rationale ?? 'Begründung', req.rationale);
    }
  }
  addLine(
    copy.edit?.fields?.category ?? 'Kategorie',
    formatCategoryLabel(item.kind === 'requirement' ? item.eventType ?? item.category : item.category, copy),
  );
  if (item.kind === 'requirement') {
    addLine('Subtyp', formatSubtypeLabel(item.eventSubtype, copy));
  }
  addLine(copy.edit?.fields?.priority ?? 'Priorität', priorityLabel || item.priority);
  if (topics.length) {
    addLine(copy.edit?.fields?.topics ?? 'Themenbereiche', topics.join(', '));
  }
  const sourceRange = item.data?.sourceRange;
  if (Number.isFinite(Number(sourceRange?.startSec)) && Number.isFinite(Number(sourceRange?.endSec))) {
    lines.push(
      `${copy.requirements?.source ?? 'Quelle'}: ${Number(sourceRange.startSec).toFixed(1)}s – ${Number(sourceRange.endSec).toFixed(1)}s`,
    );
  }
  addLine(copy.requirements?.speaker ?? 'Sprecher', sourceRange?.speaker);
  addLine(copy.header?.customer ?? 'Kunde', conversation?.customerId);
  addLine(copy.header?.project ?? 'Projekt', conversation?.projectId);
  addLine(copy.header?.meeting ?? 'Meeting', formatMeetingDate(conversation?.meetingDate));
  addLine(copy.header?.conversationId ?? 'Conversation ID', conversation?.conversationId || conversation?.id);
  addLine(copy.header?.status ?? 'Status', conversation?.status);
  addLine(copy.header?.created ?? 'Erstellt', formatDateTime(conversation?.createdAt));
  addLine(copy.header?.updated ?? 'Aktualisiert', formatDateTime(conversation?.updatedAt));
  lines.push('');
  lines.push(JSON.stringify(item.data ?? item, null, 2));
  return lines.join('\n');
};

const buildItems = (requirements, stories, copy) => {
  const items = [];
  (requirements ?? []).forEach((req, index) => {
    const id =
      normalizeValue(req?.eventId) ||
      normalizeValue(req?.id) ||
      normalizeValue(req?.reqId) ||
      normalizeValue(req?.requirementId) ||
      normalizeValue(req?.sortKey) ||
      `req-${index + 1}`;
    const text = normalizeValue(req?.text || req?.requirement);
    const categories = normalizeList(req?.categories);
    const tags = normalizeList(req?.tags);
    const type = normalizeEventType(req?.eventType || req?.type, 'sonstiges');
    const subtype = req?.eventSubtype ?? '';
    const topics = uniqueTopics([type, ...tags, ...categories]);
    items.push({
      kind: 'requirement',
      kindLabel: copy.sections.requirements,
      id,
      sortKey: normalizeValue(req?.sortKey) || id,
      text,
      priority: normalizeValue(req?.priority),
      eventType: type,
      eventSubtype: subtype,
      category: type || categories[0] || '',
      topics,
      status: formatRequirementStatus(req?.status),
      data: req,
    });
  });

  (stories ?? []).forEach((story, index) => {
    const id = normalizeValue(story?.id) || `story-${index + 1}`;
    const categories = normalizeList(story?.categories);
    items.push({
      kind: 'userStory',
      kindLabel: copy.sections.userStories,
      id,
      sortKey: normalizeValue(story?.sortKey) || id,
      text: normalizeValue(story?.story),
      priority: normalizeValue(story?.priority),
      category: categories[0] || '',
      topics: uniqueTopics(categories),
      data: story,
    });
  });

  return items;
};

const normalizeDeferredDecisions = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item, index) => {
      const text = normalizeValue(item?.text || item?.decision || item?.summary);
      if (!text) return null;
      const sourceRange = item?.sourceRange && typeof item.sourceRange === 'object'
        ? {
          startSec: Number(item.sourceRange.startSec),
          endSec: Number(item.sourceRange.endSec),
          speaker: normalizeValue(item.sourceRange.speaker),
        }
        : null;
      const rationale = normalizeValue(item?.rationale);
      const status = normalizeValue(item?.status || 'DEFERRED');
      const options = Array.isArray(item?.options)
        ? item.options.map((entry) => normalizeValue(entry)).filter(Boolean)
        : [];
      return {
        id: normalizeValue(item?.decisionId || item?.id || item?.reqId || `deferred-${index + 1}`),
        text,
        facet: normalizeValue(item?.facet),
        sourceRange: sourceRange && Number.isFinite(sourceRange.startSec) && Number.isFinite(sourceRange.endSec)
          ? sourceRange
          : null,
        rationale,
        status,
        options,
      };
    })
    .filter(Boolean);

const buildTopicOptions = (items) => {
  const map = new Map();
  items.forEach((item) => {
    (item.topics ?? []).forEach((topic) => {
      const label = normalizeValue(topic);
      if (!label) return;
      const key = toTopicKey(label);
      const entry = map.get(key) ?? { key, label, count: 0 };
      entry.count += 1;
      map.set(key, entry);
    });
  });
  return Array.from(map.values()).sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.label.localeCompare(b.label);
  });
};

const buildFilterOptions = (items, copy) => {
  const kinds = uniqueByKey(items.map((item) => item.kind)).map((entry) => ({
    key: entry.key,
    label:
      entry.key === 'requirement'
        ? copy.sections.requirements
        : entry.key === 'userstory'
          ? copy.sections.userStories
          : entry.label,
  }));
  const priorities = uniqueByKey(items.map((item) => item.priority));
  const categories = uniqueByKey(items.map((item) => item.category));
  const topics = buildTopicOptions(items);
  return { kinds, priorities, categories, topics };
};

const applyFilters = (items, filters) => {
  const query = toKey(filters.query);
  const topicKey = filters.topic;
  return items.filter((item) => {
    if (filters.kind !== 'all' && toKey(item.kind) !== filters.kind) return false;
    if (filters.priority !== 'all' && toKey(item.priority) !== filters.priority) return false;
    if (filters.category !== 'all' && toKey(item.category) !== filters.category) return false;
    if (topicKey !== 'all') {
      const hasTopic = (item.topics ?? []).some((topic) => toTopicKey(topic) === topicKey);
      if (!hasTopic) return false;
    }
    if (!query) return true;

    const meta = [
      item.id,
      item.text,
      item.category,
      item.priority,
      ...(item.topics ?? []),
      ...(item.kind === 'userStory'
        ? [item.data?.role, item.data?.goal, item.data?.benefit]
        : []),
    ]
      .map((value) => normalizeValue(value))
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return meta.includes(query);
  });
};

const renderFilters = (filterData, filters, copy) => {
  const topics = filterData.topics;
  const topicHtml = topics.length
    ? html`
        <div class="row filter-pill-row">
          <button
            type="button"
            class="pill-toggle ${filters.topic === 'all' ? 'active' : ''}"
            data-topic="all"
          >
            ${copy.filters.topicsAll}
          </button>
          ${topics
            .map(
              (topic) => html`
                <button
                  type="button"
                  class="pill-toggle ${filters.topic === topic.key ? 'active' : ''}"
                  data-topic="${topic.key}"
                >
                  ${escapeHtml(formatTopicLabel(topic.label, copy))}
                  <span class="topic-count">${topic.count}</span>
                </button>
              `,
            )
            .join('')}
        </div>
      `
    : html`<p class="muted small">${copy.filters.noTopics}</p>`;

  const kindOptions = filterData.kinds
    .map(
      (kind) =>
        `<option value="${kind.key}" ${filters.kind === kind.key ? 'selected' : ''}>${escapeHtml(kind.label)}</option>`,
    )
    .join('');
  const priorityOptions = filterData.priorities
    .map(
      (priority) =>
        `<option value="${priority.key}" ${filters.priority === priority.key ? 'selected' : ''}>${escapeHtml(
          formatPriorityLabel(priority.label, copy),
        )}</option>`,
    )
    .join('');
  const categoryOptions = filterData.categories
    .map(
      (category) =>
        `<option value="${category.key}" ${filters.category === category.key ? 'selected' : ''}>${escapeHtml(
          formatCategoryLabel(category.label, copy),
        )}</option>`,
    )
    .join('');

  return html`
    <div class="conversation-filters stack">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <span class="muted small">${copy.filters.topics}</span>
        <button type="button" class="ghost-button small" data-clear-filters="true">
          ${copy.filters.clear}
        </button>
      </div>
      ${topicHtml}
      <div class="form-grid filter-grid">
        <div class="stack tight">
          <label class="muted small">${copy.filters.contentLabel}</label>
          <select id="filter-kind">
            <option value="all" ${filters.kind === 'all' ? 'selected' : ''}>${copy.filters.contentAll}</option>
            ${kindOptions}
          </select>
        </div>
        <div class="stack tight">
          <label class="muted small">${copy.filters.priorityLabel}</label>
          <select id="filter-priority">
            <option value="all" ${filters.priority === 'all' ? 'selected' : ''}>${copy.filters.priorityAll}</option>
            ${priorityOptions}
          </select>
        </div>
        <div class="stack tight">
          <label class="muted small">${copy.filters.categoryLabel}</label>
          <select id="filter-category">
            <option value="all" ${filters.category === 'all' ? 'selected' : ''}>${copy.filters.categoryAll}</option>
            ${categoryOptions}
          </select>
        </div>
        <div class="stack tight">
          <label class="muted small">${copy.filters.searchLabel}</label>
          <input
            type="search"
            id="filter-query"
            placeholder="${escapeAttribute(copy.filters.searchPlaceholder)}"
            value="${escapeAttribute(filters.query)}"
          />
        </div>
      </div>
      <span class="muted small" id="result-count"></span>
    </div>
  `;
};

const renderTopics = (topics, copy) =>
  topics.length
    ? html`<div class="row topic-chip-row">${topics
        .map((topic) => `<span class="pill">${escapeHtml(formatTopicLabel(topic, copy))}</span>`)
        .join('')}</div>`
    : '';

const renderRequirementCard = (item, state, copy, filterData, requirementsCopy) => {
  const isEditing = state.editingId === item.id;
  const priorityLabel = item.priority ? formatPriorityLabel(item.priority, copy) : '';
  const statusValue = formatRequirementStatus(item.data?.status ?? item.status);
  const statusLabels = requirementsCopy?.status ?? {};
  const statusOptions = REQUIREMENT_STATUS_ORDER.includes(statusValue)
    ? REQUIREMENT_STATUS_ORDER
    : [statusValue, ...REQUIREMENT_STATUS_ORDER];
  const resolvedKey = normalizeValue(item.sortKey || item.data?.sortKey || item.id);
  const savingKey = `${state.conversationId}:${resolvedKey}`;
  const isSaving = state.saving?.has(savingKey);
  const sourceLine = formatSourceRange(item.data?.sourceRange, copy);
  const priorityValues = filterData.priorities.map((entry) => entry.label);
  const editPriorityOptions = uniqueByKey([item.priority, ...priorityValues])
    .map(
      (priority) =>
        `<option value="${escapeAttribute(priority.label)}" ${
          toKey(item.priority) === priority.key ? 'selected' : ''
        }>${escapeHtml(formatPriorityLabel(priority.label, copy))}</option>`,
    )
    .join('');

  return html`
    <div class="card conversation-item stack" data-item-id="${escapeAttribute(item.id)}">
      <div class="row" style="justify-content: space-between; align-items: flex-start;">
        <div class="stack tight">
          <div class="row">
            <span class="badge">${escapeHtml(item.kindLabel)}</span>
            <span class="pill">${escapeHtml(formatCategoryLabel(item.eventType ?? item.category ?? 'sonstiges', copy))}</span>
            ${item.eventSubtype ? `<span class="pill">${escapeHtml(formatSubtypeLabel(item.eventSubtype, copy))}</span>` : ''}
          </div>
          ${priorityLabel ? `<span class="muted small">${copy.edit.fields.priority}: ${escapeHtml(priorityLabel)}</span>` : ''}
        </div>
        <div class="conversation-item-controls">
          <select
            class="conversation-status-select"
            data-status-select
            data-item-id="${escapeAttribute(item.id)}"
            data-sort-key="${escapeAttribute(resolvedKey)}"
            aria-label="${escapeAttribute(requirementsCopy.table?.status ?? 'Status')}"
            ${!resolvedKey || isSaving ? 'disabled' : ''}
          >
            ${statusOptions
              .map((option) => {
                const label = statusLabels[option] ?? option;
                return `<option value="${option}" ${option === statusValue ? 'selected' : ''}>${escapeHtml(label)}</option>`;
              })
              .join('')}
          </select>
          <div class="row conversation-item-actions">
            <button
              type="button"
              class="ghost-button small icon-button"
              data-action="copy"
              aria-label="${escapeAttribute(copy.edit.copyButton ?? 'Kopieren')}"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M16 1H6c-1.1 0-2 .9-2 2v12h2V3h10V1zm3 4H10c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h9c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H10V7h9v14z"
                />
              </svg>
            </button>
            <button
              type="button"
              class="ghost-button small icon-button"
              data-action="edit"
              aria-label="${escapeAttribute(copy.edit.editButton ?? 'Bearbeiten')}"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l8.06-8.06.92.92L5.92 19.58zM20.71 7.04a1 1 0 0 0 0-1.41L18.37 3.3a1 1 0 0 0-1.41 0l-1.62 1.62 3.75 3.75 1.62-1.62z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <p>${escapeHtml(item.text || copy.requirements.empty)}</p>
      ${item.data?.rationale ? `<p class="muted small">${copy.requirements.rationale}: ${escapeHtml(item.data.rationale)}</p>` : ''}
      ${sourceLine ? `<p class="muted small">${escapeHtml(sourceLine)}</p>` : ''}
      ${renderTopics(item.topics ?? [], copy)}
      ${
        isEditing
          ? html`
              <div class="edit-panel stack" data-edit-panel="true">
                <div class="stack tight">
                  <label class="muted small">${copy.edit.fields.text}</label>
                  <textarea data-field="text">${escapeHtml(item.text)}</textarea>
                </div>
                <div class="form-grid edit-grid">
                  <div class="stack tight">
                    <label class="muted small">${copy.edit.fields.category}</label>
                    <input type="text" data-field="category" value="${escapeAttribute(item.category)}" />
                  </div>
                  <div class="stack tight">
                    <label class="muted small">${copy.edit.fields.priority}</label>
                    <select data-field="priority">
                      <option value="">${copy.edit.priorityUnset}</option>
                      ${editPriorityOptions}
                    </select>
                  </div>
                  <div class="stack tight">
                    <label class="muted small">${copy.edit.fields.topics}</label>
                    <input
                      type="text"
                      data-field="topics"
                      placeholder="${escapeAttribute(copy.edit.topicsPlaceholder)}"
                      value="${escapeAttribute((item.topics ?? []).join(', '))}"
                    />
                  </div>
                </div>
                <div class="row" style="justify-content: flex-end;">
                  <button type="button" class="ghost-button" data-action="cancel">${copy.edit.cancelButton}</button>
                  <button type="button" class="button" data-action="save">${copy.edit.saveButton}</button>
                </div>
              </div>
            `
          : ''
      }
    </div>
  `;
};

const renderUserStoryCard = (item, state, copy, filterData) => {
  const story = item.data ?? {};
  const isEditing = state.editingId === item.id;
  const priorityLabel = item.priority ? formatPriorityLabel(item.priority, copy) : '';
  const sourceLine = formatSourceRange(story.sourceRange, copy);
  const priorityValues = filterData.priorities.map((entry) => entry.label);
  const editPriorityOptions = uniqueByKey([item.priority, ...priorityValues])
    .map(
      (priority) =>
        `<option value="${escapeAttribute(priority.label)}" ${
          toKey(item.priority) === priority.key ? 'selected' : ''
        }>${escapeHtml(formatPriorityLabel(priority.label, copy))}</option>`,
    )
    .join('');

  return html`
    <div class="card conversation-item stack" data-item-id="${escapeAttribute(item.id)}">
      <div class="row" style="justify-content: space-between;">
        <div class="row">
          <span class="badge">${escapeHtml(item.kindLabel)}</span>
          ${item.category ? `<span class="pill">${escapeHtml(formatCategoryLabel(item.category, copy))}</span>` : ''}
        </div>
        <div class="row">
          ${priorityLabel ? `<span class="pill">${escapeHtml(priorityLabel)}</span>` : ''}
          <button
            type="button"
            class="ghost-button small icon-button"
            data-action="copy"
            aria-label="${escapeAttribute(copy.edit.copyButton ?? 'Kopieren')}"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path
                fill="currentColor"
                d="M16 1H6c-1.1 0-2 .9-2 2v12h2V3h10V1zm3 4H10c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h9c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H10V7h9v14z"
              />
            </svg>
          </button>
          <button
            type="button"
            class="ghost-button small icon-button"
            data-action="edit"
            aria-label="${escapeAttribute(copy.edit.editButton ?? 'Bearbeiten')}"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path
                fill="currentColor"
                d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l8.06-8.06.92.92L5.92 19.58zM20.71 7.04a1 1 0 0 0 0-1.41L18.37 3.3a1 1 0 0 0-1.41 0l-1.62 1.62 3.75 3.75 1.62-1.62z"
              />
            </svg>
          </button>
        </div>
      </div>
      <strong>${escapeHtml(item.text || copy.userStories.empty)}</strong>
      <p class="muted small">
        ${copy.userStories.role}: ${escapeHtml(story.role || '–')} ·
        ${copy.userStories.goal}: ${escapeHtml(story.goal || '–')} ·
        ${copy.userStories.benefit}: ${escapeHtml(story.benefit || '–')}
      </p>
      ${
        Array.isArray(story.acceptance) && story.acceptance.length
          ? html`
              <details>
                <summary>${copy.userStories.acceptance}</summary>
                <ul>
                  ${story.acceptance.map((line) => `<li>${escapeHtml(line)}</li>`).join('')}
                </ul>
              </details>
            `
          : ''
      }
      ${sourceLine ? `<p class="muted small">${escapeHtml(sourceLine)}</p>` : ''}
      ${renderTopics(item.topics ?? [], copy)}
      ${
        isEditing
          ? html`
              <div class="edit-panel stack" data-edit-panel="true">
                <div class="stack tight">
                  <label class="muted small">${copy.edit.fields.story}</label>
                  <textarea data-field="story">${escapeHtml(item.text)}</textarea>
                </div>
                <div class="form-grid edit-grid">
                  <div class="stack tight">
                    <label class="muted small">${copy.edit.fields.role}</label>
                    <input type="text" data-field="role" value="${escapeAttribute(story.role)}" />
                  </div>
                  <div class="stack tight">
                    <label class="muted small">${copy.edit.fields.goal}</label>
                    <input type="text" data-field="goal" value="${escapeAttribute(story.goal)}" />
                  </div>
                  <div class="stack tight">
                    <label class="muted small">${copy.edit.fields.benefit}</label>
                    <input type="text" data-field="benefit" value="${escapeAttribute(story.benefit)}" />
                  </div>
                </div>
                <div class="stack tight">
                  <label class="muted small">${copy.edit.fields.acceptance}</label>
                  <textarea data-field="acceptance">${escapeHtml((story.acceptance ?? []).join('\n'))}</textarea>
                </div>
                <div class="form-grid edit-grid">
                  <div class="stack tight">
                    <label class="muted small">${copy.edit.fields.category}</label>
                    <input type="text" data-field="category" value="${escapeAttribute(item.category)}" />
                  </div>
                  <div class="stack tight">
                    <label class="muted small">${copy.edit.fields.priority}</label>
                    <select data-field="priority">
                      <option value="">${copy.edit.priorityUnset}</option>
                      ${editPriorityOptions}
                    </select>
                  </div>
                  <div class="stack tight">
                    <label class="muted small">${copy.edit.fields.topics}</label>
                    <input
                      type="text"
                      data-field="topics"
                      placeholder="${escapeAttribute(copy.edit.topicsPlaceholder)}"
                      value="${escapeAttribute((item.topics ?? []).join(', '))}"
                    />
                  </div>
                </div>
                <div class="row" style="justify-content: flex-end;">
                  <button type="button" class="ghost-button" data-action="cancel">${copy.edit.cancelButton}</button>
                  <button type="button" class="button" data-action="save">${copy.edit.saveButton}</button>
                </div>
              </div>
            `
          : ''
      }
    </div>
  `;
};

const renderItems = (items, state, copy, filterData, requirementsCopy) => {
  if (!items.length) {
    const isFiltered =
      state.filters.kind !== 'all' ||
      state.filters.priority !== 'all' ||
      state.filters.category !== 'all' ||
      state.filters.topic !== 'all' ||
      state.filters.query.trim().length > 0;
    return `<p class="muted">${isFiltered ? copy.filters.noResults : copy.filters.emptyAll}</p>`;
  }
  const requirementItems = items.filter((item) => item.kind === 'requirement');
  const storyItems = items.filter((item) => item.kind === 'userStory');

  const groupedRequirementSections = eventTypeOrder
    .map((type) => {
      const entries = requirementItems.filter((item) =>
        normalizeEventType(item?.eventType ?? item?.data?.eventType ?? item?.category, 'sonstiges') === type
      );
      if (!entries.length) return '';
      const groupTitle = labelForEventType(type, copy?.requirementsList?.types ?? copy?.types ?? {});
      return html`
        <section class="stack tight conversation-event-group">
          <div class="row" style="justify-content: space-between; align-items: center;">
            <h4>${escapeHtml(groupTitle)}</h4>
            <span class="muted small">${entries.length}</span>
          </div>
          ${entries.map((item) => renderRequirementCard(item, state, copy, filterData, requirementsCopy)).join('')}
        </section>
      `;
    })
    .filter(Boolean)
    .join('');

  return html`
    <div class="stack">
      ${groupedRequirementSections}
      ${storyItems.map((item) => renderUserStoryCard(item, state, copy, filterData)).join('')}
    </div>
  `;
};

const renderExecutiveSummary = (state, copy) => {
  const summary = normalizeSummaryLine(state.summary);
  const executiveSummary = state.executiveSummary;
  const hasContent =
    Boolean(summary) ||
    Boolean(executiveSummary?.overview) ||
    (executiveSummary?.keyRequirements?.length ?? 0) > 0 ||
    (executiveSummary?.openDecisions?.length ?? 0) > 0 ||
    (executiveSummary?.risks?.length ?? 0) > 0 ||
    (executiveSummary?.nextSteps?.length ?? 0) > 0;
  if (!hasContent) return '';

  const preview = buildSummaryPreviewSentences(executiveSummary, summary);
  const block = (title, values, fallback = '—') =>
    html`
      <div class="summary-block stack tight">
        <h4>${escapeHtml(title)}</h4>
        ${
          Array.isArray(values) && values.length
            ? `<ul>${values.map((entry) => `<li>${escapeHtml(entry)}</li>`).join('')}</ul>`
            : `<p>${escapeHtml(fallback)}</p>`
        }
      </div>
    `;

  return html`
    <section class="conversation-summary">
      <div class="card summary-panel stack">
        <div class="row summary-header-row">
          <h3>${escapeHtml(copy.summary?.title ?? 'Executive Summary')}</h3>
          <div class="row summary-actions">
            <button type="button" class="ghost-button conversation-action-button" data-summary-download>
              ${escapeHtml(copy.summary?.download ?? 'Download')}
            </button>
            <button type="button" class="ghost-button conversation-action-button" data-summary-toggle>
              ${
                state.summaryExpanded
                  ? escapeHtml(copy.summary?.collapse ?? 'Einklappen')
                  : escapeHtml(copy.summary?.expand ?? 'Ausklappen')
              }
            </button>
          </div>
        </div>
        ${summary ? `<p class="summary-short">${escapeHtml(summary)}</p>` : ''}
        ${
          preview.length
            ? `<ul class="summary-preview">${preview.map((line) => `<li>${escapeHtml(line)}</li>`).join('')}</ul>`
            : ''
        }
        ${
          state.summaryExpanded && executiveSummary
            ? html`
                <div class="summary-details">
                  <div class="summary-block stack tight">
                    <h4>${escapeHtml(copy.summary?.overview ?? 'Kurzüberblick')}</h4>
                    <p>${escapeHtml(executiveSummary.overview || '—')}</p>
                  </div>
                  ${block(copy.summary?.keyRequirements ?? 'Kernanforderungen', executiveSummary.keyRequirements)}
                  ${block(copy.summary?.openDecisions ?? 'Offene Entscheidungen', executiveSummary.openDecisions)}
                  ${block(copy.summary?.risks ?? 'Risiken', executiveSummary.risks)}
                  ${block(copy.summary?.nextSteps ?? 'Nächste Schritte', executiveSummary.nextSteps)}
                </div>
              `
            : ''
        }
      </div>
    </section>
  `;
};

const renderDeferredDecisions = (decisions, copy) => {
  const title = copy.sections?.deferredDecisions ?? 'Vertagte Entscheidungen';
  if (!Array.isArray(decisions) || !decisions.length) {
    return html`
      <div class="card stack">
        <h3>${title}</h3>
        <p class="muted small">Keine vertagten Entscheidungen vorhanden.</p>
      </div>
    `;
  }
  return html`
    <div class="card stack">
      <div class="row" style="justify-content: space-between;">
        <h3>${title}</h3>
        <span class="muted small">${decisions.length}</span>
      </div>
      <div class="stack tight">
        ${decisions
          .map((decision, index) => {
            const source = decision.sourceRange ? formatSourceRange(decision.sourceRange, copy) : '';
            const options = Array.isArray(decision.options) ? decision.options : [];
            const facetLabel = decision.facet
              ? `Offenes Detail: ${decision.facet === 'color' ? 'Farbe' : decision.facet === 'count' ? 'Anzahl' : decision.facet === 'time' ? 'Zeitpunkt' : 'Detail'}`
              : '';
            return html`
              <div class="stack tight" style="padding: 0.5rem 0; border-bottom: 1px solid var(--border-color);">
                <strong>${index + 1}. ${escapeHtml(decision.text)}</strong>
                <span class="muted small">${escapeHtml(decision.status || 'DEFERRED')}</span>
                ${source ? `<span class="muted small">${escapeHtml(source)}</span>` : ''}
                ${facetLabel ? `<span class="muted small">${escapeHtml(facetLabel)}</span>` : ''}
                ${options.length ? `<span class="muted small">Optionen: ${escapeHtml(options.join(' | '))}</span>` : ''}
                ${decision.rationale ? `<span class="muted small">${escapeHtml(decision.rationale)}</span>` : ''}
              </div>
            `;
          })
          .join('')}
      </div>
    </div>
  `;
};

const renderLog = (state, copy) => {
  const entries = Array.isArray(state.changeLog)
    ? [...state.changeLog].sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
    : [];
  if (!entries.length) {
    return html`
      <div class="card conversation-log stack">
        <h3>${copy.log.title}</h3>
        <p class="muted small">${copy.log.empty}</p>
        <p class="muted small">${copy.log.note}</p>
      </div>
    `;
  }

  const labelMap = {
    text: copy.edit.fields.text,
    story: copy.edit.fields.story,
    role: copy.edit.fields.role,
    goal: copy.edit.fields.goal,
    benefit: copy.edit.fields.benefit,
    acceptance: copy.edit.fields.acceptance,
    category: copy.edit.fields.category,
    priority: copy.edit.fields.priority,
    topics: copy.edit.fields.topics,
  };

  return html`
    <div class="card conversation-log stack">
      <div class="row" style="justify-content: space-between;">
        <h3>${copy.log.title}</h3>
        <span class="muted small">${entries.length}</span>
      </div>
      <div class="stack tight">
        ${entries
          .map((entry) => {
            const fieldLabel = labelMap[entry.field] ?? entry.field;
            const from = normalizeValue(entry.from) || copy.log.emptyValue;
            const to = normalizeValue(entry.to) || copy.log.emptyValue;
            const kindLabel =
              entry.kind === 'requirement' ? copy.sections.requirements : copy.sections.userStories;
            return html`
              <div class="log-entry">
                <span class="muted small">${new Date(entry.at).toLocaleString()}</span>
                <span>
                  ${escapeHtml(kindLabel)} ${escapeHtml(entry.id)} · ${escapeHtml(fieldLabel)}:
                  ${escapeHtml(from)} → ${escapeHtml(to)}
                </span>
              </div>
            `;
          })
          .join('')}
      </div>
      <p class="muted small">${copy.log.note}</p>
    </div>
  `;
};

export const renderConversationView = async (container, conversationId) => {
  const copy = getConversationCopy();
  const qaCopy = getQaChatCopy();
  const requirementsCopy = getRequirementsListCopy();
  const commonCopy = getCommonCopy();
  container.innerHTML = html`
    <div class="card stack">
      <h2>${copy.title} <code>${escapeHtml(conversationId)}</code></h2>
      <p class="muted">${copy.loading}</p>
    </div>
  `;

  try {
    const [data, templatesData] = await Promise.all([
      getResults(conversationId),
      getExportTemplates().catch(() => ({ systemTemplates: [], userTemplates: [] })),
    ]);
    const conversation = data?.conversation ?? {};
    const allTemplates = [
      ...(templatesData.systemTemplates ?? []),
      ...(templatesData.userTemplates ?? []),
    ];
    const items = buildItems(data?.events ?? data?.requirements, data?.userStories, copy);
    const deferredDecisions = normalizeDeferredDecisions(
      data?.deferredDecisions ?? data?.artifacts?.deferredDecisions ?? [],
    );
    const executiveSummary = normalizeExecutiveSummary(data?.executiveSummary);
    const summary = normalizeSummaryLine(data?.summary);
    const sourceLabel = getConversationSourceLabel(conversation);
    const engineLabel = getConversationEngineLabel(conversation);

    const state = {
      conversationId,
      conversation,
      items,
      deferredDecisions,
      executiveSummary,
      summary,
      summaryExpanded: false,
      filters: {
        kind: 'all',
        priority: 'all',
        category: 'all',
        topic: 'all',
        query: '',
      },
      editingId: null,
      saving: new Set(),
      changeLog: Array.isArray(conversation.changeLog) ? conversation.changeLog : [],
    };

    const editTarget = sessionStorage.getItem(EDIT_KEY);
    const shouldAutoScroll = editTarget === conversationId;
    if (shouldAutoScroll) {
      sessionStorage.removeItem(EDIT_KEY);
      const firstEditable = state.items.find((item) => item.kind === 'requirement') || state.items[0];
      state.editingId = firstEditable?.id ?? null;
    }

    // Group templates by format
    const templatesByFormat = {
      csv: allTemplates.filter((t) => t.format === 'csv'),
      json: allTemplates.filter((t) => t.format === 'json'),
      md: allTemplates.filter((t) => t.format === 'md'),
      xlsx: allTemplates.filter((t) => t.format === 'xlsx'),
      docx: allTemplates.filter((t) => t.format === 'docx'),
    };

    const backKey = sessionStorage.getItem(BACK_KEY);
    const backTarget =
      backKey === 'dashboard' ? '#/dashboard' : backKey === 'requirements' ? '#/requirements' : '#/jobs';

    container.innerHTML = html`
      <div class="card stack">
        <header class="stack conversation-header">
          <div class="row" style="justify-content: space-between; align-items: center;">
            <div class="row" style="align-items: center; gap: 8px;">
              <button type="button" class="ghost-button conversation-action-button" id="back-to-jobs">
                ${copy.backLabel ?? copy.backToJobs}
              </button>
              <div data-conversation-status-region>
                ${renderStatusProgress(conversation, copy)}
              </div>
            </div>
            <div class="row" style="position: relative; gap: 0.5rem; align-items: center;">
              <button type="button" class="ghost-button conversation-action-button" id="edit-job">
                ${copy.metaEdit?.button ?? 'Bearbeiten'}
              </button>
              <button type="button" class="button-danger conversation-action-button" id="delete-job">
                ${copy.delete?.button ?? 'Job löschen'}
              </button>
              <button type="button" class="conversation-action-button" id="download-overlay-btn">
                ${commonCopy?.download?.button ?? 'Download'}
              </button>
            </div>
          </div>
          <div class="stack tight">
            <h2 class="conversation-title">${escapeHtml(formatConversationTitle(conversation, copy))}</h2>
            <div class="row muted small conversation-meta">
              <span data-meta-created>${copy.header.created}: ${formatDateTime(conversation.createdAt)}</span>
              <span data-meta-updated>· ${copy.header.updated}: ${formatDateTime(conversation.updatedAt)}</span>
              <span data-meta-meeting>· ${copy.header.meeting ?? 'Meeting'}: ${formatMeetingDate(conversation.meetingDate)}</span>
              <span>
                · ${copy.header.conversationId}: <code>${escapeHtml(conversation.conversationId ?? conversationId)}</code>
              </span>
              <span>· Quelle: <span class="pill">${escapeHtml(sourceLabel)}</span></span>
              <span>· Engine: <span class="pill">${escapeHtml(engineLabel)}</span></span>
            </div>
          </div>
        </header>
        <div id="conversation-meta-edit" class="edit-panel stack" style="display: none;" aria-hidden="true">
          <div class="form-grid">
            <div class="stack tight">
              <label class="muted small">${copy.header.customer}</label>
              <input type="text" id="meta-customer" placeholder="${escapeAttribute(copy.header.customer)}" />
            </div>
            <div class="stack tight">
              <label class="muted small">${copy.header.project}</label>
              <input type="text" id="meta-project" placeholder="${escapeAttribute(copy.header.project)}" />
            </div>
            <div class="stack tight">
              <label class="muted small">${copy.header.meeting ?? 'Meeting'}</label>
              <input type="date" id="meta-meeting" />
            </div>
          </div>
          <div class="row" style="gap: 0.5rem;">
            <button type="button" class="button conversation-action-button" id="meta-save">
              ${copy.metaEdit?.save ?? 'Speichern'}
            </button>
            <button type="button" class="ghost-button conversation-action-button" id="meta-cancel">
              ${copy.metaEdit?.cancel ?? 'Abbrechen'}
            </button>
          </div>
        </div>
        <section id="conversation-qa-chat" class="qa-chat-section"></section>
        <div id="conversation-summary"></div>
        <div id="conversation-filters"></div>
        <div id="conversation-list"></div>
        <div id="conversation-deferred"></div>
        <div id="conversation-log"></div>
      </div>
    `;

    const downloadButton = container.querySelector('#download-overlay-btn');
    const backButton = container.querySelector('#back-to-jobs');
    const editButton = container.querySelector('#edit-job');
    const deleteButton = container.querySelector('#delete-job');
    const metaEditPanel = container.querySelector('#conversation-meta-edit');
    const metaCustomerInput = container.querySelector('#meta-customer');
    const metaProjectInput = container.querySelector('#meta-project');
    const metaMeetingInput = container.querySelector('#meta-meeting');
    const metaSaveButton = container.querySelector('#meta-save');
    const metaCancelButton = container.querySelector('#meta-cancel');
    const statusRegionEl = container.querySelector('[data-conversation-status-region]');
    const metaUpdatedEl = container.querySelector('[data-meta-updated]');
    const metaMeetingEl = container.querySelector('[data-meta-meeting]');
    const titleEl = container.querySelector('.conversation-title');
    const summaryEl = container.querySelector('#conversation-summary');
    const filtersEl = container.querySelector('#conversation-filters');
    const listEl = container.querySelector('#conversation-list');
    const deferredEl = container.querySelector('#conversation-deferred');
    const logEl = container.querySelector('#conversation-log');
    let closeDownloadOverlay = null;

    const buildQaContext = () => {
      const customerId = normalizeValue(state.conversation?.customerId ?? state.conversation?.customer);
      const projectId = normalizeValue(state.conversation?.projectId ?? state.conversation?.project);
      return conversationQaContext({
        conversationId,
        customerId,
        projectId,
      });
    };

    const qaPanel = createQaChatPanel({
      copy: qaCopy,
      title: qaCopy.conversationTitle,
      description: qaCopy.conversationDescription,
      context: buildQaContext(),
    });
    qaPanel.setHost(container.querySelector('#conversation-qa-chat'));

    let filterData = buildFilterOptions(state.items, copy);

    const updateResultCount = (count) => {
      const countEl = filtersEl.querySelector('#result-count');
      if (countEl) {
        countEl.textContent = formatTemplate(copy.filters.countLabel, { count });
      }
    };

    const syncActiveTopics = () => {
      filtersEl.querySelectorAll('[data-topic]').forEach((button) => {
        button.classList.toggle('active', button.dataset.topic === state.filters.topic);
      });
    };

    const renderStatusRegion = () => {
      if (statusRegionEl) {
        statusRegionEl.innerHTML = renderStatusProgress(conversation, copy);
      }
    };

    let statusPollId = null;
    let statusPollInFlight = false;

    const stopStatusPolling = () => {
      if (!statusPollId) return;
      clearInterval(statusPollId);
      statusPollId = null;
    };

    const shouldPollStatus = () => !isTerminalConversationStatus(conversation?.status);

    const refreshConversationStatus = async () => {
      if (statusPollInFlight) return;
      if (!shouldPollStatus()) {
        stopStatusPolling();
        return;
      }

      statusPollInFlight = true;
      try {
        const previousStatus = normalizeValue(conversation?.status).toUpperCase();
        const latestConversation = await getConversation(conversationId);
        if (!latestConversation || typeof latestConversation !== 'object') return;

        Object.assign(conversation, latestConversation);
        state.conversation = conversation;
        renderStatusRegion();
        if (metaUpdatedEl) {
          metaUpdatedEl.textContent = `· ${copy.header.updated}: ${formatDateTime(conversation.updatedAt)}`;
        }

        const nextStatus = normalizeValue(conversation?.status).toUpperCase();
        if (isTerminalConversationStatus(nextStatus)) {
          stopStatusPolling();
          if (nextStatus !== previousStatus) {
            await renderConversationView(container, conversationId);
          }
        }
      } catch (pollError) {
        console.error('Failed to refresh conversation status', pollError);
      } finally {
        statusPollInFlight = false;
      }
    };

    if (shouldPollStatus()) {
      statusPollId = window.setInterval(() => {
        void refreshConversationStatus();
      }, 5000);
    }

    const downloadExecutiveSummary = async () => {
      try {
        const url = await getExportUrl(conversationId, 'executive-summary');
        const link = document.createElement('a');
        link.href = url;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSuccess(copy.summary?.downloadSuccess ?? 'Executive Summary wird heruntergeladen.');
      } catch (error) {
        console.error('Failed to download executive summary', error);
        showError(copy.summary?.downloadError ?? 'Executive Summary konnte nicht geladen werden.');
      }
    };

    const renderSummarySection = () => {
      if (!summaryEl) return;
      summaryEl.innerHTML = renderExecutiveSummary(state, copy);
      const toggleButton = summaryEl.querySelector('[data-summary-toggle]');
      if (toggleButton) {
        toggleButton.addEventListener('click', () => {
          state.summaryExpanded = !state.summaryExpanded;
          renderSummarySection();
        });
      }
      const downloadSummaryButton = summaryEl.querySelector('[data-summary-download]');
      if (downloadSummaryButton) {
        downloadSummaryButton.addEventListener('click', () => {
          void downloadExecutiveSummary();
        });
      }
    };

    const renderFiltersSection = () => {
      filterData = buildFilterOptions(state.items, copy);
      if (state.filters.kind !== 'all' && !filterData.kinds.some((entry) => entry.key === state.filters.kind)) {
        state.filters.kind = 'all';
      }
      if (
        state.filters.priority !== 'all' &&
        !filterData.priorities.some((entry) => entry.key === state.filters.priority)
      ) {
        state.filters.priority = 'all';
      }
      if (
        state.filters.category !== 'all' &&
        !filterData.categories.some((entry) => entry.key === state.filters.category)
      ) {
        state.filters.category = 'all';
      }
      if (state.filters.topic !== 'all' && !filterData.topics.some((entry) => entry.key === state.filters.topic)) {
        state.filters.topic = 'all';
      }
      filtersEl.innerHTML = renderFilters(filterData, state.filters, copy);
    };

    const renderListSection = () => {
      const filteredItems = applyFilters(state.items, state.filters);
      listEl.innerHTML = renderItems(filteredItems, state, copy, filterData, requirementsCopy);
      updateResultCount(filteredItems.length);
    };

    const renderDeferredSection = () => {
      if (!deferredEl) return;
      deferredEl.innerHTML = renderDeferredDecisions(state.deferredDecisions, copy);
    };

    const renderLogSection = () => {
      logEl.innerHTML = renderLog(state, copy);
    };

    const updateRequirementStatus = async (item, nextStatus, selectEl) => {
      if (!item) return;
      const sortKey = normalizeValue(item.sortKey || item.data?.sortKey || item.id);
      if (!conversationId || !sortKey) {
        showError(requirementsCopy.errors?.save ?? 'Status konnte nicht gespeichert werden.');
        return;
      }
      const normalized = formatRequirementStatus(nextStatus);
      const previous = formatRequirementStatus(item.data?.status ?? item.status);
      const savingKey = `${conversationId}:${sortKey}`;
      state.saving.add(savingKey);
      if (selectEl) selectEl.disabled = true;
      item.status = normalized;
      if (item.data) {
        item.data.status = normalized;
      }
      try {
        const response = await updateConversationItem(conversationId, {
          kind: 'event',
          sortKey,
          updates: { status: normalized },
        });
        const updatedItem = response?.item;
        if (updatedItem) {
          item.status = formatRequirementStatus(updatedItem.status);
          item.data = { ...item.data, ...updatedItem };
          if (selectEl) selectEl.value = item.status;
        }
        if (Array.isArray(response?.changeLog)) {
          state.changeLog = response.changeLog;
          renderLogSection();
        }
        showSuccess(requirementsCopy.toast?.statusSaved ?? 'Status aktualisiert.');
      } catch (error) {
        console.error('Failed to update requirement status', error);
        item.status = previous;
        if (item.data) {
          item.data.status = previous;
        }
        if (selectEl) selectEl.value = previous;
        showError(requirementsCopy.errors?.save ?? 'Status konnte nicht gespeichert werden.');
      } finally {
        state.saving.delete(savingKey);
        if (selectEl) selectEl.disabled = false;
      }
    };

    renderSummarySection();
    renderFiltersSection();
    renderListSection();
    renderDeferredSection();
    renderLogSection();

    if (shouldAutoScroll && state.editingId && listEl) {
      requestAnimationFrame(() => {
        const selector = `[data-item-id="${CSS.escape(state.editingId)}"]`;
        const card = listEl.querySelector(selector);
        if (card && typeof card.scrollIntoView === 'function') {
          card.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    if (backButton) {
      backButton.addEventListener('click', () => {
        window.location.hash = backTarget;
      });
    }

    const formatOptions = [
      {
        id: 'csv',
        label: 'CSV',
        description: copy.exports?.csv ?? 'CSV Export',
      },
      {
        id: 'md',
        label: 'Markdown',
        description: copy.exports?.md ?? 'Markdown Export',
      },
      {
        id: 'executive-summary',
        label: copy.summary?.title ?? 'Executive Summary',
        description: copy.summary?.downloadDescription ?? 'Executive Summary (Markdown)',
      },
      {
        id: 'events-json',
        label: 'Events JSON',
        description: 'Ereignisse als JSON',
      },
      {
        id: 'json',
        label: 'JSON',
        description: 'Ereignisse + User Stories',
        templateFormat: 'json',
      },
      {
        id: 'xlsx',
        label: 'Excel',
        description: 'Vorlage erforderlich',
        requiresTemplate: true,
        templateFormat: 'xlsx',
      },
      {
        id: 'docx',
        label: 'Word',
        description: 'Vorlage erforderlich',
        requiresTemplate: true,
        templateFormat: 'docx',
      },
    ];

    const formatToType = {
      csv: 'csv',
      md: 'md',
      'executive-summary': 'executive-summary',
      'events-json': 'events-json',
    };

    const triggerDownload = async ({ format, templateId }) => {
      if (templateId) {
        const url = await getExportUrl(conversationId, templateId);
        const link = document.createElement('a');
        link.href = url;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      if (format === 'json') {
        const requirements = state.items.filter((item) => item.kind === 'requirement').map((item) => item.data);
        const userStories = state.items.filter((item) => item.kind === 'userStory').map((item) => item.data);
        const payload = {
          conversationId,
          events: requirements,
          requirements,
          userStories,
        };
        const filename = `events_${conversationId.slice(0, 8)}.json`;
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return;
      }

      const exportId = formatToType[format];
      if (!exportId) {
        throw new Error('Bitte eine Vorlage auswählen.');
      }
      const url = await getExportUrl(conversationId, exportId);
      const link = document.createElement('a');
      link.href = url;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    if (downloadButton) {
      downloadButton.addEventListener('click', () => {
        if (closeDownloadOverlay) closeDownloadOverlay();
        closeDownloadOverlay = openDownloadOverlay({
          title: 'Download',
          subtitle: formatTemplate(copy.filters.countLabel, { count: state.items.length }),
          commonCopy,
          formats: formatOptions,
          templatesByFormat,
          manageTemplatesHref: '#/export-templates',
          onConfirm: triggerDownload,
        });
      });
    }

    const openMetaEdit = () => {
      if (!metaEditPanel || !metaCustomerInput || !metaProjectInput || !metaMeetingInput) return;
      metaCustomerInput.value = normalizeValue(conversation.customerId || conversation.customer) || '';
      metaProjectInput.value = normalizeValue(conversation.projectId || conversation.project) || '';
      metaMeetingInput.value = normalizeValue(conversation.meetingDate) || getTodayDateString();
      metaEditPanel.style.display = 'block';
      metaEditPanel.setAttribute('aria-hidden', 'false');
      metaCustomerInput.focus();
    };

    const closeMetaEdit = () => {
      if (metaEditPanel) {
        metaEditPanel.style.display = 'none';
        metaEditPanel.setAttribute('aria-hidden', 'true');
      }
    };

    if (editButton) {
      editButton.addEventListener('click', () => {
        openMetaEdit();
      });
    }

    if (metaCancelButton) {
      metaCancelButton.addEventListener('click', closeMetaEdit);
    }

    if (metaSaveButton && metaCustomerInput && metaProjectInput && metaMeetingInput) {
      metaSaveButton.addEventListener('click', async () => {
        const nextCustomer = normalizeValue(metaCustomerInput.value) || null;
        const nextProject = normalizeValue(metaProjectInput.value) || null;
        const nextMeeting = normalizeValue(metaMeetingInput.value) || getTodayDateString();
        const originalLabel = metaSaveButton.textContent;
        metaSaveButton.disabled = true;
        metaSaveButton.textContent = copy.metaEdit?.inProgress ?? 'Speichere…';
        try {
          const response = await updateConversation(conversationId, {
            customerId: nextCustomer,
            projectId: nextProject,
            meetingDate: nextMeeting,
          });
          const updated = response?.conversation ?? response ?? {};
          conversation.customerId = updated.customerId ?? nextCustomer;
          conversation.projectId = updated.projectId ?? nextProject;
          conversation.meetingDate = updated.meetingDate ?? nextMeeting;
          if (updated.updatedAt) {
            conversation.updatedAt = updated.updatedAt;
          }
          if (titleEl) {
            titleEl.textContent = formatConversationTitle(conversation, copy);
          }
          if (metaUpdatedEl) {
            metaUpdatedEl.textContent = `· ${copy.header.updated}: ${formatDateTime(conversation.updatedAt)}`;
          }
          if (metaMeetingEl) {
            metaMeetingEl.textContent = `· ${copy.header.meeting ?? 'Meeting'}: ${formatMeetingDate(conversation.meetingDate)}`;
          }
          qaPanel.setContext(buildQaContext());
          closeMetaEdit();
        } catch (error) {
          console.error('Failed to update conversation meta', error);
          alert(error?.message || copy.metaEdit?.error || 'Aktualisierung fehlgeschlagen.');
        } finally {
          metaSaveButton.disabled = false;
          metaSaveButton.textContent = originalLabel;
        }
      });
    }

    if (deleteButton) {
      deleteButton.addEventListener('click', async () => {
        const confirmText =
          copy.delete?.confirm ??
          'Diesen Job wirklich löschen? Alle Anforderungen, Transkripte und Dateien werden entfernt.';
        if (!window.confirm(confirmText)) return;
        const originalLabel = deleteButton.textContent;
        deleteButton.disabled = true;
        deleteButton.textContent = copy.delete?.inProgress ?? 'Lösche…';
        try {
          await deleteConversation(conversationId);
          alert(copy.delete?.success ?? 'Job wurde gelöscht.');
          window.location.hash = backTarget;
        } catch (error) {
          console.error('Failed to delete conversation', error);
          alert(error?.message || copy.delete?.error || 'Löschen fehlgeschlagen.');
          deleteButton.disabled = false;
          deleteButton.textContent = originalLabel;
        }
      });
    }

    if (filtersEl) {
      filtersEl.addEventListener('click', (event) => {
        const topicButton = event.target.closest('[data-topic]');
        if (topicButton) {
          state.filters.topic = topicButton.dataset.topic || 'all';
          syncActiveTopics();
          renderListSection();
          return;
        }
        const clearButton = event.target.closest('[data-clear-filters]');
        if (clearButton) {
          state.filters = { kind: 'all', priority: 'all', category: 'all', topic: 'all', query: '' };
          filtersEl.querySelectorAll('select').forEach((select) => {
            select.value = 'all';
          });
          const queryInput = filtersEl.querySelector('#filter-query');
          if (queryInput) queryInput.value = '';
          syncActiveTopics();
          renderListSection();
        }
      });

      filtersEl.addEventListener('input', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
        if (target.id === 'filter-kind') state.filters.kind = target.value || 'all';
        if (target.id === 'filter-priority') state.filters.priority = target.value || 'all';
        if (target.id === 'filter-category') state.filters.category = target.value || 'all';
        if (target.id === 'filter-query') state.filters.query = target.value || '';
        renderListSection();
      });
    }

    if (listEl) {
      listEl.addEventListener('change', (event) => {
        const select = event.target.closest('[data-status-select]');
        if (!select) return;
        const itemId = select.dataset.itemId;
        const item = state.items.find((entry) => entry.id === itemId);
        if (!item || item.kind !== 'requirement') return;
        updateRequirementStatus(item, select.value, select);
      });

      listEl.addEventListener('click', async (event) => {
        const actionButton = event.target.closest('[data-action]');
        if (!actionButton) return;
        const card = actionButton.closest('[data-item-id]');
        if (!card) return;
        const itemId = card.dataset.itemId;
        const item = state.items.find((entry) => entry.id === itemId);
        if (!item) return;
        const action = actionButton.dataset.action;
        if (action === 'copy') {
          try {
            const text = buildItemCopyText(item, state.conversation, copy);
            await copyToClipboard(text);
            showSuccess(copy.edit.copySuccess ?? 'Inhalt kopiert.');
          } catch (error) {
            console.error('Copy failed', error);
            showError(copy.edit.copyError ?? 'Kopieren fehlgeschlagen.');
          }
          return;
        }
        if (action === 'edit') {
          state.editingId = item.id;
          renderListSection();
          return;
        }
        if (action === 'cancel') {
          state.editingId = null;
          renderListSection();
          return;
        }
        if (action === 'save') {
          const categoryInput = card.querySelector('[data-field="category"]');
          const priorityInput = card.querySelector('[data-field="priority"]');
          const topicsInput = card.querySelector('[data-field="topics"]');
          const nextCategory = normalizeValue(categoryInput?.value);
          const nextPriority = normalizeValue(priorityInput?.value);
          let nextTopics = uniqueTopics(normalizeList(topicsInput?.value));
          if (nextCategory) {
            const hasCategory = nextTopics.some((topic) => toTopicKey(topic) === toTopicKey(nextCategory));
            if (!hasCategory) {
              nextTopics = [nextCategory, ...nextTopics];
            }
          }

          const sortKey = item.sortKey || item.data?.sortKey || item.id;
          const payload = {
            kind: item.kind === 'requirement' ? 'event' : item.kind,
            sortKey,
            updates: {
              category: nextCategory,
              priority: nextPriority,
              topics: nextTopics,
            },
          };

          if (item.kind === 'requirement') {
            const textInput = card.querySelector('[data-field="text"]');
            payload.updates.text = normalizeValue(textInput?.value);
          } else {
            const storyInput = card.querySelector('[data-field="story"]');
            const roleInput = card.querySelector('[data-field="role"]');
            const goalInput = card.querySelector('[data-field="goal"]');
            const benefitInput = card.querySelector('[data-field="benefit"]');
            const acceptanceInput = card.querySelector('[data-field="acceptance"]');
            payload.updates.story = normalizeValue(storyInput?.value);
            payload.updates.role = normalizeValue(roleInput?.value);
            payload.updates.goal = normalizeValue(goalInput?.value);
            payload.updates.benefit = normalizeValue(benefitInput?.value);
            payload.updates.acceptance = parseAcceptance(acceptanceInput?.value);
          }

          try {
            const response = await updateConversationItem(conversationId, payload);
            const updatedItem = response?.item;
            if (updatedItem) {
              if (item.kind === 'requirement') {
                const categories = normalizeList(updatedItem.categories);
                const tags = normalizeList(updatedItem.tags);
                const type = normalizeEventType(updatedItem.eventType || updatedItem.type, 'sonstiges');
                item.text = normalizeValue(updatedItem.text || updatedItem.requirement) || item.text;
                item.priority = normalizeValue(updatedItem.priority) || '';
                item.eventType = type;
                item.eventSubtype = normalizeValue(updatedItem.eventSubtype) || '';
                item.category = type || categories[0] || '';
                item.topics = uniqueTopics([type, ...tags, ...categories]);
              } else {
                const categories = normalizeList(updatedItem.categories);
                item.text = normalizeValue(updatedItem.story) || item.text;
                item.priority = normalizeValue(updatedItem.priority) || '';
                item.category = categories[0] || '';
                item.topics = uniqueTopics(categories);
                item.data = {
                  ...item.data,
                  ...updatedItem,
                };
              }
              item.sortKey = normalizeValue(updatedItem.sortKey) || item.sortKey;
              item.data = item.kind === 'requirement' ? { ...item.data, ...updatedItem } : item.data;
            }
            if (Array.isArray(response?.changeLog)) {
              state.changeLog = response.changeLog;
            }
            state.editingId = null;
            renderFiltersSection();
            renderListSection();
            renderLogSection();
            syncActiveTopics();
          } catch (error) {
            console.error('Failed to update item', error);
            alert(error?.message || 'Änderung konnte nicht gespeichert werden.');
          }
        }
      });
    }

    registerViewCleanup(() => {
      if (statusPollId) {
        clearInterval(statusPollId);
        statusPollId = null;
      }
      if (downloadButton) downloadButton.replaceWith(downloadButton.cloneNode(true));
      if (backButton) backButton.replaceWith(backButton.cloneNode(true));
      if (editButton) editButton.replaceWith(editButton.cloneNode(true));
      if (deleteButton) deleteButton.replaceWith(deleteButton.cloneNode(true));
      if (metaSaveButton) metaSaveButton.replaceWith(metaSaveButton.cloneNode(true));
      if (metaCancelButton) metaCancelButton.replaceWith(metaCancelButton.cloneNode(true));
      if (closeDownloadOverlay) closeDownloadOverlay();
      qaPanel.destroy();
    });
  } catch (error) {
    container.innerHTML = html`
      <div class="card stack">
        <h2>${copy.title} <code>${escapeHtml(conversationId)}</code></h2>
        <p class="muted">${escapeHtml(error.message)}</p>
      </div>
    `;
  }
};
